import * as React from 'react';
import styled from 'styled-components';

import { Icon } from '../../primitives/Icon';

import type { MaskedFieldConfig } from './types';

type MaskedState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'revealed'; value: string }
  | { status: 'hidden'; value: string };

type MaskedAction =
  | { type: 'REVEAL_START' }
  | { type: 'REVEAL_SUCCESS'; value: string }
  | { type: 'REVEAL_ERROR' }
  | { type: 'TOGGLE_HIDE' }
  | { type: 'TOGGLE_SHOW' };

function maskedReducer(state: MaskedState, action: MaskedAction): MaskedState {
  switch (action.type) {
    case 'REVEAL_START':
      return { status: 'loading' };
    case 'REVEAL_SUCCESS':
      return { status: 'revealed', value: action.value };
    case 'REVEAL_ERROR':
      return { status: 'idle' };
    case 'TOGGLE_HIDE':
      if (state.status === 'revealed') return { status: 'hidden', value: state.value };
      return state;
    case 'TOGGLE_SHOW':
      if (state.status === 'hidden') return { status: 'revealed', value: state.value };
      return state;
  }
}

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ToggleButton = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export interface MaskedValueProps {
  config: MaskedFieldConfig;
  entityId: string;
}

export function MaskedValue({ config, entityId }: MaskedValueProps) {
  const { placeholder, unmaskFn, formatValue } = config;
  const [state, dispatch] = React.useReducer(maskedReducer, { status: 'idle' });

  const handleToggle = React.useCallback(async () => {
    if (state.status === 'idle') {
      dispatch({ type: 'REVEAL_START' });
      try {
        const raw = await unmaskFn(entityId);
        dispatch({ type: 'REVEAL_SUCCESS', value: raw });
      } catch {
        dispatch({ type: 'REVEAL_ERROR' });
      }
    } else if (state.status === 'revealed') {
      dispatch({ type: 'TOGGLE_HIDE' });
    } else if (state.status === 'hidden') {
      dispatch({ type: 'TOGGLE_SHOW' });
    }
  }, [state.status, unmaskFn, entityId]);

  const displayText = React.useMemo(() => {
    if (state.status === 'revealed') {
      return formatValue != null ? formatValue(state.value) : state.value;
    }
    return placeholder;
  }, [state, placeholder, formatValue]);

  const isVisible = state.status === 'revealed';
  const isLoading = state.status === 'loading';
  const iconName = isVisible ? 'visibility' : 'visibility_off';

  return (
    <Wrapper>
      <span>{displayText}</span>
      <ToggleButton
        type='button'
        onClick={handleToggle}
        disabled={isLoading}
        aria-label={isVisible ? 'Hide value' : 'Reveal value'}
      >
        <Icon name={isLoading ? 'progress_activity' : iconName} size='sm' />
      </ToggleButton>
    </Wrapper>
  );
}
